import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, ShieldCheck, ClipboardCheck, CircleDot, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader, Rich, Loading, EmptyState, formatDate } from '../components/Shared';
import { usePitaLogo } from '../components/PitaCTA';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const KPI = ({ icon: Icon, label, value, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5 flex items-center gap-4">
      <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#1a2744] shrink-0"><Icon className="h-5 w-5 text-[#C9A84C]" /></span>
      <div>
        <p className="text-2xl font-black text-[#1a2744] leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const pitaLogo = usePitaLogo('pita_page');

  useEffect(() => {
    Promise.allSettled([
      api.get('/reports').then((r) => setReports(r.data || [])),
      api.get('/issues/stats').then((r) => setStats(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader pageKey="reports" kicker="PUBLIC ACCOUNTABILITY" title="Accountability Reports" subtitle="What Ward 8 raised, what was answered, and what is still outstanding, published in the open every month." logo={pitaLogo?.url} logoAlt={pitaLogo?.alt} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

        {/* Live public stats */}
        {stats && (
          <div className="mb-10">
            <SectionHeader kicker="LIVE HUB SNAPSHOT" title="What residents are reporting" dek="Live counts from the Ward 8 Public Issue Tracker App (PITA). Only safe aggregates are shown, never names, addresses, or private details." />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reports-kpi-row">
              <KPI icon={ClipboardCheck} label="Total reports submitted" value={stats.total} testId="reports-kpi-total" />
              <KPI icon={CircleDot} label="Currently open" value={stats.open} testId="reports-kpi-open" />
              <KPI icon={CheckCircle2} label="Resolved or closed" value={stats.resolved} testId="reports-kpi-resolved" />
            </div>
            {stats.top_concerns?.length > 0 && (
              <Card className="mt-4" data-testid="reports-top-concerns-chart">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="font-serif font-bold text-[#1a2744] text-lg">Top concerns by category</h3>
                  <div className="h-64 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.top_concerns} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#1a2744" radius={[0, 4, 4, 0]} barSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Generated {formatDate(stats.generated_at)} · Live data from PITA</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Privacy assurance */}
        <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 mb-10 flex gap-3" data-testid="reports-privacy-note">
          <ShieldCheck className="h-5 w-5 text-[#1a2744] shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">Accountability reports contain only public-safe summaries and totals. Resident names, contact details, and exact addresses are never published.</p>
        </div>

        {/* Monthly reports */}
        <SectionHeader kicker="MONTHLY REPORTS" title="Ward 8 accountability, on the record" />
        {reports.length === 0 ? (
          <EmptyState title="No reports published yet" subtitle="Monthly accountability reports will be published here as they are completed." />
        ) : (
          <div className="space-y-6">
            {reports.map((r) => (
              <Card key={r.id} className="card-lift" data-testid="report-card">
                <CardContent className="p-5 sm:p-8">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#C9A84C] uppercase tracking-wider"><FileText className="h-4 w-4" /> {r.period}</div>
                  <h2 className="font-serif font-black text-[#1a2744] text-xl sm:text-2xl mt-1.5">{r.title}</h2>
                  <p className="text-xs text-slate-500 mt-1">Published {formatDate(r.published_at)}</p>
                  <div className="mt-4"><Rich html={r.body_html} /></div>
                  {r.pdf_url && (
                    <Button asChild variant="outline" className="mt-4 h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="report-download-button">
                      <a href={fileUrl(r.pdf_url)} target="_blank" rel="noopener noreferrer"><Download className="h-4 w-4 mr-1.5" /> Download PDF</a>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-10 border-[#C9A84C]" data-testid="reports-cta-card">
          <CardContent className="p-6 sm:p-8">
            <h3 className="font-serif font-black text-[#1a2744] text-lg">Have something that belongs in the next report?</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Report your issue through the Issue Tracker so it gets documented, tracked, and counted.</p>
            <Button asChild className="mt-4 h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="reports-report-cta">
              <Link to="/issue-tracker/report">Report an Issue</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
